import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type RateLimitEntry } from '../services/api'
import { useState, useMemo } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Globe } from 'lucide-react'

// ─── helpers ────────────────────────────────────────────────────────────────

function statusColor(s: RateLimitEntry['status']) {
  if (s === 'blocked') return 'badge-error'
  if (s === 'warn') return 'badge-warning'
  return 'badge-success'
}

function barColor(s: 'ok' | 'warn' | 'blocked') {
  if (s === 'blocked') return 'progress-error'
  if (s === 'warn') return 'progress-warning'
  return 'progress-success'
}

function UsageBar({ pct, status }: { pct: number; status: 'ok' | 'warn' | 'blocked' }) {
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <progress className={`progress ${barColor(status)} w-20`} value={Math.min(pct, 100)} max={100} />
      <span className="text-xs tabular-nums opacity-70">{pct.toFixed(1)}%</span>
    </div>
  )
}

type SortKey =
  | 'name'
  | 'access_tier'
  | 'status'
  | 'consumed_tokens_today'
  | 'daily_token_budget'
  | 'consumed_responses_today'
  | 'daily_response_budget'

function SortableHeader({
  label,
  column,
  sortKey,
  direction,
  onSort,
}: {
  label: string
  column: SortKey
  sortKey: SortKey
  direction: 'asc' | 'desc'
  onSort: (column: SortKey) => void
}) {
  const active = sortKey === column
  const Icon = active ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <th>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-primary"
        onClick={() => onSort(column)}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-35'}`} />
      </button>
    </th>
  )
}

// ─── inline edit cell ────────────────────────────────────────────────────────

function EditableCell({
  value,
  onSave,
  placeholder = 'No limit',
}: {
  value: number | null
  onSave: (v: number | null) => void
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [raw, setRaw] = useState(value?.toString() ?? '')

  const commit = () => {
    const parsed = raw.trim() === '' ? null : parseInt(raw, 10)
    if (parsed !== null && (isNaN(parsed) || parsed < 0)) return
    onSave(parsed)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="number"
          min={0}
          className="input input-bordered input-xs w-28"
          placeholder={placeholder}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') setEditing(false)
          }}
          onBlur={commit}
        />
      </div>
    )
  }

  return (
    <button
      className="group flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
      onClick={() => { setRaw(value?.toString() ?? ''); setEditing(true) }}
      title="Click to edit"
    >
      {value != null
        ? <span className="font-mono">{value.toLocaleString()}</span>
        : <span className="opacity-40 italic">{placeholder}</span>}
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  )
}

// ─── global defaults card ────────────────────────────────────────────────────

function GlobalDefaultsCard() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['rate-limits-defaults'],
    queryFn: api.getGlobalDefaults,
    staleTime: 30000,
  })

  const mutation = useMutation({
    mutationFn: api.updateGlobalDefaults,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-limits-defaults'] })
      queryClient.invalidateQueries({ queryKey: ['rate-limits'] })
    },
  })

  if (isLoading) return null

  return (
    <div className="card bg-base-200 shadow border border-base-300 mb-6">
      <div className="card-body py-4 px-6">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="h-4 w-4 opacity-60" />
          <h2 className="font-semibold text-sm">Global Defaults</h2>
          <span className="text-xs opacity-50">Applied to users with no personal limit set</span>
        </div>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <span className="text-sm opacity-70 w-32">Daily tokens</span>
            <EditableCell
              value={data?.default_daily_token_budget ?? null}
              placeholder="No default"
              onSave={(v) => mutation.mutate({
                default_daily_token_budget: v,
                default_daily_response_budget: data?.default_daily_response_budget ?? null,
              })}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm opacity-70 w-32">Daily responses</span>
            <EditableCell
              value={data?.default_daily_response_budget ?? null}
              placeholder="No default"
              onSave={(v) => mutation.mutate({
                default_daily_token_budget: data?.default_daily_token_budget ?? null,
                default_daily_response_budget: v,
              })}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── bulk action bar ─────────────────────────────────────────────────────────

function BulkBar({
  count,
  onApply,
  onClear,
}: {
  count: number
  onApply: (tokenBudget: number | null, responseBudget: number | null) => void
  onClear: () => void
}) {
  const [tokenVal, setTokenVal] = useState('')
  const [respVal, setRespVal] = useState('')

  const apply = () => {
    const t = tokenVal.trim() === '' ? undefined : parseInt(tokenVal, 10)
    const r = respVal.trim() === '' ? undefined : parseInt(respVal, 10)
    if ((t !== undefined && isNaN(t)) || (r !== undefined && isNaN(r))) return
    onApply(
      tokenVal.trim() === '' ? null : (t as number),
      respVal.trim() === '' ? null : (r as number),
    )
    setTokenVal('')
    setRespVal('')
  }

  return (
    <div className="sticky top-0 z-10 bg-primary text-primary-content px-6 py-3 flex items-center gap-4 shadow-lg rounded-box mb-4">
      <span className="font-semibold text-sm">{count} user{count !== 1 ? 's' : ''} selected</span>
      <div className="flex items-center gap-2 flex-1">
        <input
          type="number"
          min={0}
          placeholder="Token budget (blank = remove)"
          className="input input-sm bg-primary-content text-base-content w-52"
          value={tokenVal}
          onChange={(e) => setTokenVal(e.target.value)}
        />
        <input
          type="number"
          min={0}
          placeholder="Response budget (blank = remove)"
          className="input input-sm bg-primary-content text-base-content w-56"
          value={respVal}
          onChange={(e) => setRespVal(e.target.value)}
        />
        <button className="btn btn-sm bg-primary-content text-primary" onClick={apply}>
          Apply to {count}
        </button>
      </div>
      <button className="btn btn-sm btn-ghost text-primary-content" onClick={onClear}>
        Deselect all
      </button>
    </div>
  )
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function RateLimits() {
  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState<'all' | RateLimitEntry['access_tier']>('all')
  const [minimumTokens, setMinimumTokens] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('consumed_tokens_today')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rate-limits'],
    queryFn: api.getRateLimits,
    staleTime: 30000,
    gcTime: 0,
  })

  const mutation = useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: Parameters<typeof api.updateRateLimit>[1] }) =>
      api.updateRateLimit(userId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rate-limits'] }),
  })

  const applyBulk = (tokenBudget: number | null, responseBudget: number | null) => {
    const patch: Parameters<typeof api.updateRateLimit>[1] = {}
    if (tokenBudget !== undefined) patch.daily_token_budget = tokenBudget
    if (responseBudget !== undefined) patch.daily_response_budget = responseBudget
    Promise.all([...selected].map((uid) => api.updateRateLimit(uid, patch))).then(() => {
      queryClient.invalidateQueries({ queryKey: ['rate-limits'] })
      setSelected(new Set())
    })
  }

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase()
    const minTokens = minimumTokens.trim() === '' ? null : Number(minimumTokens)
    const rows = data.filter((entry) => {
      const matchesName = !q
        || entry.name?.toLowerCase().includes(q)
        || entry.email?.toLowerCase().includes(q)
        || entry.user_id.includes(q)
      const matchesAccess = accessFilter === 'all' || entry.access_tier === accessFilter
      const matchesTokens = minTokens === null || Number.isNaN(minTokens)
        || entry.consumed_tokens_today >= minTokens
      return matchesName && matchesAccess && matchesTokens
    })

    const valueFor = (entry: RateLimitEntry): string | number => {
      switch (sortKey) {
        case 'name': return (entry.name || entry.email || '').toLowerCase()
        case 'access_tier': return entry.access_tier
        case 'status': return entry.status
        case 'consumed_tokens_today': return entry.consumed_tokens_today
        case 'daily_token_budget': return entry.daily_token_budget ?? -1
        case 'consumed_responses_today': return entry.consumed_responses_today
        case 'daily_response_budget': return entry.daily_response_budget ?? -1
      }
    }

    return [...rows].sort((left, right) => {
      const a = valueFor(left)
      const b = valueFor(right)
      const comparison = typeof a === 'string' && typeof b === 'string'
        ? a.localeCompare(b)
        : Number(a) - Number(b)
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [accessFilter, data, minimumTokens, search, sortDirection, sortKey])

  const handleSort = (column: SortKey) => {
    if (sortKey === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(column)
      setSortDirection(column === 'name' ? 'asc' : 'desc')
    }
  }

  const allSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.user_id))

  const toggleAll = () => {
    if (allSelected) {
      setSelected((s) => { const n = new Set(s); filtered.forEach((e) => n.delete(e.user_id)); return n })
    } else {
      setSelected((s) => { const n = new Set(s); filtered.forEach((e) => n.add(e.user_id)); return n })
    }
  }

  const toggleOne = (id: string) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  const summary = useMemo(() => {
    if (!data) return { total: 0, blocked: 0, warn: 0 }
    return {
      total: data.length,
      blocked: data.filter((e) => e.status === 'blocked').length,
      warn: data.filter((e) => e.status === 'warn').length,
    }
  }, [data])

  if (isLoading) return (
    <div className="p-8">
      <h1 className="page-title mb-6">Rate Limits</h1>
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg" />
      </div>
    </div>
  )

  if (error) return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="card bg-base-100 shadow-xl max-w-md border border-base-300">
        <div className="card-body items-center text-center">
          <h2 className="card-title">Failed to load rate limits</h2>
          <p className="opacity-80">{error instanceof Error ? error.message : 'Unknown error'}</p>
          <button className="btn btn-primary mt-4" onClick={() => refetch()}>Try Again</button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="page-title">Rate Limits</h1>
        <div className="flex gap-2">
          {summary.blocked > 0 && <div className="badge badge-error badge-lg">{summary.blocked} blocked</div>}
          {summary.warn > 0 && <div className="badge badge-warning badge-lg">{summary.warn} warning</div>}
          <div className="badge badge-ghost badge-lg">{summary.total} users</div>
        </div>
      </div>

      <GlobalDefaultsCard />

      <div className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by name, email, or ID…"
          className="input input-bordered w-full max-w-md"
          value={search}
          onChange={(e) => { setSearch(e.target.value) }}
        />
        <select
          className="select select-bordered"
          value={accessFilter}
          onChange={(event) => setAccessFilter(event.target.value as 'all' | RateLimitEntry['access_tier'])}
          aria-label="Filter by access tier"
        >
          <option value="all">All access tiers</option>
          <option value="subscribed">Subscribed</option>
          <option value="free_trial">Free trial</option>
          <option value="staff">Staff</option>
        </select>
        <input
          type="number"
          min={0}
          placeholder="Minimum tokens today"
          className="input input-bordered w-56"
          value={minimumTokens}
          onChange={(event) => setMinimumTokens(event.target.value)}
        />
      </div>

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onApply={applyBulk}
          onClear={() => setSelected(new Set())}
        />
      )}

      <div className="card bg-base-200 shadow-xl">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={allSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  <SortableHeader label="User" column="name" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Access" column="access_tier" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Status" column="status" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Tokens today" column="consumed_tokens_today" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Daily token budget" column="daily_token_budget" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Responses today" column="consumed_responses_today" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Daily response budget" column="daily_response_budget" sortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => (
                  <tr key={entry.user_id} className={`hover ${selected.has(entry.user_id) ? 'bg-base-300' : ''}`}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selected.has(entry.user_id)}
                        onChange={() => toggleOne(entry.user_id)}
                      />
                    </td>
                    <td>
                      <div className="font-medium">{entry.name || 'Unknown'}</div>
                      <div className="text-xs opacity-50">{entry.email || entry.user_id}</div>
                    </td>
                    <td>
                      <span className="badge badge-sm badge-outline">
                        {entry.access_tier === 'free_trial'
                          ? 'Free trial'
                          : entry.access_tier === 'subscribed' ? 'Subscribed' : 'Staff'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${statusColor(entry.status)}`}>
                        {entry.status === 'ok' ? 'OK' : entry.status === 'warn' ? 'Warning' : 'Blocked'}
                      </span>
                    </td>
                    <td>
                      <div className="text-sm tabular-nums">{entry.consumed_tokens_today.toLocaleString()}</div>
                      {entry.effective_token_budget != null && (
                        <UsageBar pct={entry.token_pct} status={entry.token_status} />
                      )}
                    </td>
                    <td>
                      <EditableCell
                        value={entry.daily_token_budget}
                        placeholder={entry.effective_token_budget != null ? `Inherits (${entry.effective_token_budget.toLocaleString()})` : 'No limit'}
                        onSave={(v) => mutation.mutate({ userId: entry.user_id, patch: { daily_token_budget: v, daily_response_budget: entry.daily_response_budget } })}
                      />
                    </td>
                    <td>
                      <div className="text-sm tabular-nums">{entry.consumed_responses_today.toLocaleString()}</div>
                      {entry.effective_response_budget != null && (
                        <UsageBar pct={entry.response_pct} status={entry.response_status} />
                      )}
                    </td>
                    <td>
                      <EditableCell
                        value={entry.daily_response_budget}
                        placeholder={entry.effective_response_budget != null ? `Inherits (${entry.effective_response_budget.toLocaleString()})` : 'No limit'}
                        onSave={(v) => mutation.mutate({ userId: entry.user_id, patch: { daily_token_budget: entry.daily_token_budget, daily_response_budget: v } })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 opacity-60">
              {search ? 'No users match your search' : 'No users found'}
            </div>
          )}
        </div>
      </div>

      <p className="text-xs opacity-40 mt-3">
        Click any budget value to edit inline. Leave blank to remove the personal limit (user inherits global default). Select rows to bulk-set budgets. Resets daily at UTC midnight.
      </p>
    </div>
  )
}
